// TOUR-2 — shell-level host for imperative target highlights (ASK-1, dev panel).

import { useEffect, useState } from "react";
import { useFireflyTargetHighlight } from "./useFireflyTargetHighlight";
import type { FireflyIntent } from "./fireflyIntents";

export type FireflyTargetHighlightHostProps = {
  targetId: string | null;
  intent?: Extract<FireflyIntent, "teaching" | "guidance">;
  callout?: { title: string; body: string };
  onDismiss?: () => void;
};

export function FireflyTargetHighlightHost({
  targetId,
  intent = "teaching",
  callout,
  onDismiss,
}: FireflyTargetHighlightHostProps) {
  const { overlay, dismiss, denied, missing } = useFireflyTargetHighlight({
    targetId,
    active: Boolean(targetId),
    intent,
    callout,
  });
  const [missedMessage, setMissedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!targetId || (!denied && !missing)) {
      setMissedMessage(null);
      return;
    }
    const message = denied
      ? "Firefly can’t highlight that control with your current access."
      : "Firefly couldn’t find that control on this screen. Try closing the current lead or switching modes, then ask again.";
    setMissedMessage(message);
    const t = window.setTimeout(() => {
      setMissedMessage(null);
      onDismiss?.();
    }, 3200);
    return () => window.clearTimeout(t);
  }, [targetId, denied, missing, onDismiss]);

  if (!overlay && !missedMessage) return null;

  return (
    <>
      {overlay}
      {missedMessage ? (
        <div
          role="status"
          className="fixed bottom-24 left-1/2 z-[9140] max-w-sm -translate-x-1/2 rounded-xl border border-amber-300 bg-white/95 px-4 py-3 text-sm text-amber-950 shadow-lg dark:border-amber-700 dark:bg-zinc-900/95 dark:text-amber-100"
        >
          {missedMessage}
        </div>
      ) : null}
      {overlay ? (
        <button
          type="button"
          aria-label="Dismiss Firefly highlight"
          className="fixed inset-0 z-[9135] cursor-default bg-transparent"
          onClick={() => {
            dismiss();
            onDismiss?.();
          }}
        />
      ) : null}
    </>
  );
}
